import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import CatalogAnalyzer from "@/pages/tools/CatalogAnalyzer";
import ListingOptimizer from "@/pages/tools/ListingOptimizer";
import KeywordGenerator from "@/pages/tools/KeywordGenerator";
import Blog from "@/pages/Blog";
import BlogDetail from "@/pages/BlogDetail";
import Waitlist from "@/pages/Waitlist";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import { AdminSignups, AdminLeads, AdminWaitlist } from "@/pages/admin/AdminTables";
import AdminBlogs from "@/pages/admin/AdminBlogs";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminExports from "@/pages/admin/AdminExports";

function PublicLayout({ children, hideChrome = false }) {
  if (hideChrome) return <>{children}</>;
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: detect Google OAuth callback fragment during render, NOT in useEffect
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<PublicLayout><Landing /></PublicLayout>} />
      <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
      <Route path="/blog/:slug" element={<PublicLayout><BlogDetail /></PublicLayout>} />
      <Route path="/waitlist" element={<PublicLayout><Waitlist /></PublicLayout>} />

      {/* Auth — no chrome */}
      <Route path="/login" element={<PublicLayout hideChrome><Login /></PublicLayout>} />
      <Route path="/signup" element={<PublicLayout hideChrome><Signup /></PublicLayout>} />

      {/* Auth-required */}
      <Route path="/dashboard" element={
        <ProtectedRoute><PublicLayout><Dashboard /></PublicLayout></ProtectedRoute>
      } />
      <Route path="/tools/catalog-analyzer" element={
        <ProtectedRoute><PublicLayout><CatalogAnalyzer /></PublicLayout></ProtectedRoute>
      } />
      <Route path="/tools/listing-optimizer" element={
        <ProtectedRoute><PublicLayout><ListingOptimizer /></PublicLayout></ProtectedRoute>
      } />
      <Route path="/tools/keyword-generator" element={
        <ProtectedRoute><PublicLayout><KeywordGenerator /></PublicLayout></ProtectedRoute>
      } />

      {/* Admin */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="signups" element={<AdminSignups />} />
        <Route path="leads" element={<AdminLeads />} />
        <Route path="waitlist" element={<AdminWaitlist />} />
        <Route path="blogs" element={<AdminBlogs />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="exports" element={<AdminExports />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}
